import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { MagnifyingGlassIcon, TicketIcon } from '@heroicons/react/24/outline';

import { getTickets } from '../api/tickets';
import { StackableModal, List, FilterDropdown } from '../components/nextgen';
import { StatusBadge, PriorityBadge } from '../components/nextgen/Badge';
import { classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'ticketsList';

const STATUS_OPTIONS = [
	{ value: 'all', label: 'All Statuses' },
	{ value: 'open', label: 'Open' },
	{ value: 'in_progress', label: 'In Progress' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'resolved', label: 'Resolved' },
	{ value: 'closed', label: 'Closed' },
];

const TicketsListModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openTicketOverview } = useModal('ticketOverview');

	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('all');

	const assetId = open?.assetId;
	const assetName = open?.assetName;

	// Fetch tickets when modal opens
	useEffect(() => {
		if (open && assetId) {
			fetchTickets();
			setSearch('');
			setSelectedStatus('all');
		}
	}, [open, assetId]);

	const fetchTickets = async (status = null) => {
		setLoading(true);
		try {
			const params = {
				assetId,
				pageSize: 100,
				sortBy: 'createdAt',
				sortOrder: 'DESC',
			};
			if (status && status !== 'all') {
				params.status = status;
			}
			const result = await getTickets(params);
			setTickets(result?.rows || []);
		} catch (error) {
			console.error('Failed to fetch tickets:', error);
			setTickets([]);
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = (status) => {
		setSelectedStatus(status);
		fetchTickets(status);
	};

	const handleClose = () => {
		setSearch('');
		setSelectedStatus('all');
		setClose();
	};

	const handleTicketClick = (ticket) => {
		openTicketOverview({ id: ticket.id });
	};

	// Check if a ticket is overdue
	const isOverdue = (ticket) => {
		if (!ticket.dueDate) return false;
		const closedStatuses = ['resolved', 'closed', 'done', 'cancelled', 'completed'];
		if (closedStatuses.includes(ticket.status)) return false;
		return moment(ticket.dueDate).isBefore(moment(), 'day');
	};

	// Check if ticket is completed/cancelled
	const isCompletedOrCancelled = (ticket) => {
		return ['resolved', 'closed', 'done', 'cancelled', 'completed'].includes(
			ticket.status,
		);
	};

	// Filter and sort tickets - completed/cancelled at bottom
	const filteredTickets = tickets
		.filter((ticket) => {
			if (!search) return true;
			const searchLower = search.toLowerCase();
			return (
				ticket.subject?.toLowerCase().includes(searchLower) ||
				ticket.description?.toLowerCase().includes(searchLower) ||
				ticket.createdBy?.firstName?.toLowerCase().includes(searchLower) ||
				ticket.createdBy?.lastName?.toLowerCase().includes(searchLower)
			);
		})
		.sort((a, b) => {
			const aCompleted = isCompletedOrCancelled(a);
			const bCompleted = isCompletedOrCancelled(b);
			if (aCompleted && !bCompleted) return 1;
			if (!aCompleted && bCompleted) return -1;
			return 0;
		});

	// Group tickets by status for summary
	const ticketStats = {
		open: tickets.filter((t) => t.status === 'open' || t.status === 'incoming')
			.length,
		inProgress: tickets.filter(
			(t) => t.status === 'in_progress' || t.status === 'inprogress',
		).length,
		resolved: tickets.filter(
			(t) => t.status === 'resolved' || t.status === 'closed' || t.status === 'done',
		).length,
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={assetName ? `Tickets for ${assetName}` : 'Asset Tickets'}
			size="3xl"
		>
			<div className="flex flex-col h-[600px]">
				{/* Stats Summary */}
				<div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
					<div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg">
						<span className="text-sm font-medium text-red-700">
							{ticketStats.open}
						</span>
						<span className="text-xs text-red-600">Open</span>
					</div>
					<div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
						<span className="text-sm font-medium text-blue-700">
							{ticketStats.inProgress}
						</span>
						<span className="text-xs text-blue-600">In Progress</span>
					</div>
					<div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
						<span className="text-sm font-medium text-gray-700">
							{ticketStats.resolved}
						</span>
						<span className="text-xs text-gray-600">Resolved/Closed</span>
					</div>
					<div className="ml-auto text-sm text-gray-500">
						{tickets.length} total tickets
					</div>
				</div>

				{/* Search and Filter Bar */}
				<div className="flex items-center gap-3 mb-4">
					<div className="relative flex-1">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search tickets..."
							className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
						/>
					</div>
					<FilterDropdown
						label="Status"
						placeholder="All Statuses"
						value={selectedStatus}
						options={STATUS_OPTIONS}
						onChange={handleStatusChange}
					/>
				</div>

				{/* Tickets List */}
				<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
					<List
						data={filteredTickets}
						loading={loading}
						keyField="id"
						bordered={false}
						emptyIcon={TicketIcon}
						emptyTitle="No tickets found"
						emptyDescription={
							search
								? 'Try a different search term'
								: selectedStatus !== 'all'
									? 'No tickets with this status'
									: 'No tickets have been reported for this asset'
						}
						renderItem={(ticket) => (
							<div
								onClick={() => handleTicketClick(ticket)}
								className={classNames(
									'flex items-center gap-3 cursor-pointer -mx-4 -my-4 px-4 py-3',
									isOverdue(ticket)
										? 'bg-red-50/50 hover:bg-red-100/50'
										: 'hover:bg-gray-50',
								)}
							>
								{/* Ticket Info */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<p className="text-sm font-medium text-gray-900 truncate">
											{ticket.subject || 'Untitled'}
										</p>
										{ticket.priority && (
											<PriorityBadge priority={ticket.priority} size="xs" />
										)}
									</div>
									<p className="text-xs text-gray-500 mt-0.5">
										{ticket.createdBy && (
											<span className="mr-1">
												{`Reported by ${ticket.createdBy.firstName || ''} ${ticket.createdBy.lastName || ''}`.trim()}
											</span>
										)}
										{ticket.createdAt && (
											<>
												<span className="mx-1">•</span>
												<span>{moment(ticket.createdAt).fromNow()}</span>
												<span className="text-gray-400 ml-1">
													({moment(ticket.createdAt).format('MMM D, YYYY')})
												</span>
											</>
										)}
									</p>
								</div>

								{/* Status Badge */}
								<div className="flex-shrink-0">
									<StatusBadge status={ticket.status} size="sm" />
								</div>
							</div>
						)}
					/>
				</div>

				{/* Footer */}
				<div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Close
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default TicketsListModal;
